import React, { useContext, useEffect } from "react";
import FilterPopup from "../FilterPopup";
import { Button } from "semantic-ui-react";
import Status from "components/Status/Status";
import ButtonGroup from "components/Atoms/ButtonGroup";
import { type StatusFilterType, getSortedStatuses, initialStatusFilter } from "./FilterTypes/StatusFilterType";
import { focusOnMount } from "utilities/reactUtils";
import { splitIntoTwoColumns } from "utilities";
import classNames from "classnames";
import { FilterContext } from "../FilterContext";
import { areArrayValuesEqual } from "../FilterGroups/instructionUtils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { VerticalKeyboardNavigationGroup } from "keyboardNavigation/KeyboardNavigationPresets";

interface Props {
  initialStatuses: server.dto.StatusSmall[]
}

const StatusFilter = (props: Props) => {
  const withoutStatusTitle = RESX.Status.WithoutStatus;
  const withStatusTitle = RESX.Status.WithStatus;

  const reset = (): StatusFilterType => ({
    selectedStatuses: [],
    initialStatuses: props.initialStatuses,
    filterType: "in"
  });

  const { register, getValue, setInitialValue, setValue } = useContext(FilterContext);
  register("statuses", {
    getTitle: (filter) => {
      if (filter.filterType === "in") { return withStatusTitle; } else { return withoutStatusTitle; }
    },
    renderSelection: (filter) => {
      return <div className="flex flex-wrap">
        {getSortedStatuses(filter).map((s, index) =>
          <React.Fragment key={s.title}>
            <Status key={s.statusId} status={s} />
            {index !== filter.selectedStatuses.length - 1 && <span className="padding-right">,</span>}
          </React.Fragment>
        )}
      </div>;
    },
    hasValue: (filter) => {
      return filter.selectedStatuses.length > 0;
    },
    reset,
    areEqual: (a, b) => {
      return a.filterType === b.filterType && areArrayValuesEqual(a.selectedStatuses.map(s => s.statusId), b.selectedStatuses.map(s => s.statusId));
    }
  });
  useEffect(() => {
    setInitialValue("statuses", reset());
  }, []);

  const [statusType, setStatusType] = React.useState<server.dto.WhereInOrNotIn>(initialStatusFilter.filterType);
  const [selectedStatuses, setSelectedStatuses] = React.useState<server.dto.StatusSmall[]>(initialStatusFilter.selectedStatuses);
  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);

  const onPopupClose = () => {
    onApply();
  };

  const onPopupOpen = () => {
    setSelectedStatuses(getValue("statuses").selectedStatuses);
    setStatusType(getValue("statuses").filterType);
    setPopupOpen(true);
  };

  const onApply = (e?) => {
    e?.preventDefault();
    if (selectedStatuses.length > 0) {
      setValue("statuses", {
        selectedStatuses,
        filterType: statusType,
        initialStatuses: props.initialStatuses ?? []
      });
    } else {
      setValue("statuses", reset());
    }
    setPopupOpen(false);
  };

  const onToggle = (status: server.dto.StatusSmall) => {
    if (selectedStatuses.some(s => s.statusId === status.statusId)) {
      setSelectedStatuses(selectedStatuses.filter(s => s.statusId !== status.statusId));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };

  const onSwitchToWithStatus = () => { setStatusType("in"); };
  const onSwitchToWithoutStatus = () => { setStatusType("notIn"); };

  const selectedStatusSet = new Set(selectedStatuses.map(s => s.statusId));

  const shoulDisplayInTwoColumns = props.initialStatuses.length > 10;
  const [firstColumn, secondColumn] = shoulDisplayInTwoColumns ? splitIntoTwoColumns(props.initialStatuses) : [[], []];

  return <div>
    <FilterPopup
      filterButtonLabel={RESX.Status.resxStatus}
      content={<form onSubmit={onApply}>
        <div className="margin-bottom">
          <ButtonGroup.Group>
            <ButtonGroup.Button
              group="withOrWithoutSatus"
              onClick={onSwitchToWithStatus}
              active={statusType === "in"}
              type="secondary"
              otherProps={{
                ref: focusOnMount
              }}
            >
              {RESX.Status.WithStatus}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="withOrWithoutSatus"
              onClick={onSwitchToWithoutStatus}
              active={statusType === "notIn"}
              type="secondary"
            >
              {RESX.Status.WithoutStatus}
            </ButtonGroup.Button>
          </ButtonGroup.Group>
        </div>
        <div className={classNames("margin-bottom scrollbox-auto", { flex: shoulDisplayInTwoColumns })} style={{ maxHeight: 300 }}>
          {!shoulDisplayInTwoColumns && props.initialStatuses.map(status => (
            <StatusRow
              key={status.statusId}
              selected={selectedStatusSet.has(status.statusId)}
              onToggle={() => { onToggle(status); }}
              status={status}
            />
          ))}
          {shoulDisplayInTwoColumns && <>
            <div style={{ flexGrow: 1 }}>
              {firstColumn.map(status => (
                <StatusRow
                  key={status.statusId}
                  selected={selectedStatusSet.has(status.statusId)}
                  onToggle={() => { onToggle(status); }}
                  status={status}
                />
              ))}
            </div>
            <div style={{ flexGrow: 1 }}>
              {secondColumn.map(status => (
                <StatusRow
                  key={status.statusId}
                  selected={selectedStatusSet.has(status.statusId)}
                  onToggle={() => { onToggle(status); }}
                  status={status}
                />
              ))}
            </div>
          </>}
        </div>

        <div>
          <Button primary fluid type="submit">{RESX.SelectionManager.Apply}</Button>
        </div>

      </form>}
      width={shoulDisplayInTwoColumns ? 600 : 300}
      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

interface StatusRowProps {
  status: server.dto.StatusSmall
  selected: boolean
  onToggle: () => void
}

const StatusRow = (props: StatusRowProps) => <div className="margin-bottom-small">
  <Checkbox
    keyboardNavigationOptions={VerticalKeyboardNavigationGroup("status")}
    onChange={props.onToggle}
    checked={props.selected}
    label={<Status status={props.status} />}
  />
</div>;

export default StatusFilter;
